/**
 * Onde se faz cada passo da prontidão do caso (E162.F2.T01, ADR-0111 §2.6). O passo e a ordem vêm
 * do servidor (`prontidao` em `GET /casos/:id/resultado`); aqui só a tela que resolve cada um.
 */
import { ptBR } from '@/i18n/pt-BR';
import type {
  MotivoDaProntidao,
  PendenciaDaProntidaoData,
  ProximoPasso,
} from '@/lib/resultadoAuditado';

// E203.F5.T08: 'protocolo', 'leitura' e 'envio' são blocos da mesa (o material ficou só com pendência
// e o protocolo fora da tela); 'pre_analise' segue como a tela de quem mostra o card do pacote.
export type TelaDoPasso =
  | 'pre_analise'
  | 'mesa'
  | 'saidas'
  | 'certificacao'
  | 'banco'
  | 'protocolo'
  | 'leitura'
  | 'envio';

/** E180.F3 (GUS-254 item 2): a âncora do campo do banco na mesa (`InformarOBanco`). */
export const ANCORA_DO_BANCO = 'informar-o-banco';

const TELA_DO_PASSO: Readonly<Record<ProximoPasso, TelaDoPasso>> = {
  receber: 'envio',
  verificar: 'protocolo',
  conferir_leitura: 'leitura',
  concluir_ccb: 'mesa',
  // A conferência das etapas e o "Registrar limitação" ficam na mesa desde a E162.F2.T02.
  corrigir_etapa: 'mesa',
  revisar_proposta: 'mesa',
  homologar: 'mesa',
  conferir_saida: 'saidas',
  // E169.F1: a marca de revisão de cada saída fica na conferência final ("Conferir e assinar").
  revisar_saidas: 'certificacao',
  assinar: 'certificacao',
  liberar: 'saidas',
  // E180.F3: o banco pendente leva, num clique, ao campo do banco do caso na mesa.
  informar_banco: 'banco',
  // E193.F1.C15 (GUS-259 item 3): a pré-análise lista o que falta ao caso parado.
  enviar_arquivos: 'envio',
  // E205.F3: as informativas se resolvem na aba Documentos (o alvo de cada uma diz o bloco).
  confirmar_leitura: 'pre_analise',
  conferir_apoio: 'pre_analise',
};

export function telaDoPasso(passo: ProximoPasso): TelaDoPasso {
  return TELA_DO_PASSO[passo];
}

/**
 * E201.F1 (GUS-47, ADR-0144): a pré-análise e a mesa são a mesa única. O passo leva à área que o
 * resolve: os documentos (receber, verificar, conferir a leitura, enviar arquivos), a conclusão ou
 * o campo do banco.
 */
export function caminhoDaTela(tela: TelaDoPasso, caseId: string): string {
  switch (tela) {
    case 'pre_analise':
      return `/casos/${caseId}/mesa#documentos`;
    // E203.F5.T08: o bloco que resolve o passo — o protocolo abre "Protocolo e documentos", o
    // envio abre "Adicionar documentos" e a leitura leva à ficha e à conferência da leitura.
    case 'protocolo':
      return `/casos/${caseId}/mesa#protocolo`;
    case 'leitura':
      return `/casos/${caseId}/mesa#leitura`;
    case 'envio':
      return `/casos/${caseId}/mesa#adicionar-documentos`;
    case 'mesa':
      return `/casos/${caseId}/mesa#conclusao`;
    case 'banco':
      return `/casos/${caseId}/mesa#${ANCORA_DO_BANCO}`;
    case 'saidas':
      return `/casos/${caseId}/saidas`;
    case 'certificacao':
      // E169.F1 (GUS-113/GUS-215): o passo abre a conferência final do caso, que diz quem assina;
      // o link direto para a tela do certificador levava quem não é certificador à página de acesso restrito.
      return `/casos/${caseId}/conferencia-final`;
  }
}

/**
 * O motivo da pendência como a tela o diz. E184.F4 (GUS-145 item 7, GT-2026-0247): o juízo pendente
 * com o que falta nomeado pelo servidor diz o que falta, em vez de só "falta material decisivo".
 * E193.F1.C15 (GUS-259 item 3): o caso parado diz os arquivos que faltam.
 */
export function textoDoMotivo(motivo: MotivoDaProntidao, faltam?: readonly string[]): string {
  const t = ptBR.resultadoAuditado;
  if (faltam !== undefined && faltam.length > 0) {
    if (motivo === 'juizo_pendente') {
      return t.prontidao.faltam.replace('{faltam}', faltam.join('; '));
    }
    if (motivo === 'aguardando_arquivos') {
      return t.prontidao.faltamArquivos.replace('{faltam}', faltam.join('; '));
    }
    // E209.F2.T09 (GUS-296 item 8): a composição que não liberou porque a saída falhou diz o motivo
    // técnico (a mesa o põe a partir do pacote, `saidaQueFalhou`).
    if (motivo === 'etapa_em_conferencia') {
      return t.prontidao.saidaQueFalhou.replace('{motivo}', faltam.join(' '));
    }
  }
  return t.motivoDaProntidao[motivo];
}

/** O motivo, a etapa (E168.F7.C04) e a peça da pendência, como a faixa e a aba Pendências dizem. */
export function porqueDaPendencia(p: PendenciaDaProntidaoData): string {
  const t = ptBR.resultadoAuditado;
  const partes: string[] = [textoDoMotivo(p.motivo, p.faltam)];
  if (p.etapa) {
    partes.push(t.prontidao.etapa.replace('{etapa}', ptBR.conferenciaDasEtapas.etapa[p.etapa]));
  }
  if (p.peca) partes.push(t.prontidao.peca.replace('{peca}', p.peca));
  return partes.join(' · ');
}
